import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { listPayouts } from "../../lib/api/contractor";
import { dateOnly, money } from "../../lib/format";
import type { Payout } from "../../lib/types/models";
import {
  Banner,
  EmptyState,
  ErrorState,
  PageHead,
  StatusPill,
  Skeleton,
  SkeletonList,
} from "@ui/react";
import { MoneyIcon } from "@ui/react/icons";
import { errorMessage } from "../../lib/errorMessage";

/**
 * Payout status vocabulary.
 *
 * There is no parser for this in lib/types/enums — payouts are the one status
 * family the shared module does not cover — so it lives here next to its only
 * consumer. The wire value is PascalCase ("Pending" is what the payment webhook
 * writes), while the mobile app compares against lowercase enum names, so the
 * comparison is case-folded exactly as `PayoutStatus.fromString` does. Unknown
 * values fall back to "pending" rather than disappearing from the summary.
 */
type PayoutStatus = "pending" | "processing" | "succeeded" | "failed";

const PAYOUT_STATUSES: readonly PayoutStatus[] = [
  "pending",
  "processing",
  "succeeded",
  "failed",
];

function parsePayoutStatus(value: string | null | undefined): PayoutStatus {
  if (!value) return "pending";
  const normalized = value.toLowerCase().replace(/[-_\s]/g, "");
  return PAYOUT_STATUSES.find((status) => status === normalized) ?? "pending";
}

interface EarningsSummary {
  total: number;
  paidOut: number;
  pending: number;
  /** Number of payouts that actually reached the contractor's bank. */
  paidCount: number;
  currency: string;
}

/**
 * `GET /payouts` returns rows and a count, never totals — the API has no
 * aggregate endpoint — so the summary is computed here, matching
 * `EarningsSummary.fromPayouts` on mobile so both surfaces agree to the rand.
 * Pending and processing are summed together: from the contractor's point of
 * view both mean "money owed but not yet in the account".
 */
function summarise(payouts: Payout[]): EarningsSummary {
  let total = 0;
  let paidOut = 0;
  let pending = 0;
  let paidCount = 0;

  for (const payout of payouts) {
    const status = parsePayoutStatus(payout.status);
    total += payout.amount;
    if (status === "succeeded") {
      paidOut += payout.amount;
      paidCount += 1;
    } else if (status === "pending" || status === "processing") {
      pending += payout.amount;
    }
  }

  return {
    total,
    paidOut,
    pending,
    paidCount,
    // Every payout in a single account settles in one currency; the first row
    // is representative, and ZAR is the fallback money() would use anyway.
    currency: payouts[0]?.currency ?? "ZAR",
  };
}

function SummaryCard({
  label,
  amount,
  currency,
  detail,
}: {
  /** Sentence case: `.label-sm` applies the uppercase transform, and a caps
   *  string in the source is read out letter by letter by some screen
   *  readers. */
  label: string;
  amount: number;
  currency: string;
  detail: string;
}) {
  return (
    <div className="card card--dense">
      <div className="stack stack--tight">
        <span className="label-sm muted">{label}</span>
        <span className="amount amount--lg">{money(amount, currency)}</span>
        <span className="muted">{detail}</span>
      </div>
    </div>
  );
}

export function EarningsScreen() {
  const [state, setState] = useState<{
    loading: boolean;
    error: unknown;
    data: Payout[] | null;
  }>({ loading: true, error: null, data: null });

  // A sequence number rather than a per-effect `ignore` flag, because the retry
  // button re-enters this while an earlier request may still be in flight.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const response = await listPayouts();
      if (seq !== requestSeq.current) return;
      setState({ loading: false, error: null, data: response.payouts ?? [] });
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setState({ loading: false, error, data: null });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const payouts = state.data;
  const summary = useMemo(() => summarise(payouts ?? []), [payouts]);

  return (
    <>
      <PageHead
        title="Earnings"
        lead="A payout is created once a homeowner signs off on the finished job."
      />

      {state.loading && !payouts ? (
        <div className="stack" role="status" aria-label="Loading your earnings">
          <div className="card-grid">
            <Skeleton height="8rem" />
            <Skeleton height="8rem" />
            <Skeleton height="8rem" />
          </div>
          <SkeletonList rows={4} height="3.5rem" />
        </div>
      ) : state.error && !payouts ? (
        <ErrorState message={errorMessage(state.error)} onRetry={() => void load()} />
      ) : payouts && payouts.length === 0 ? (
        <EmptyState title="No payouts yet" icon={<MoneyIcon />}>
          A payout is created once a homeowner signs off on the finished job.
          Complete your first job and it will show up here.
        </EmptyState>
      ) : payouts ? (
        <div className="stack stack--loose">
          <div className="card-grid">
            <SummaryCard
              label="Total"
              amount={summary.total}
              currency={summary.currency}
              detail="All payouts, all time"
            />
            <SummaryCard
              label="Paid out"
              amount={summary.paidOut}
              currency={summary.currency}
              detail={`${summary.paidCount} ${summary.paidCount === 1 ? "payment" : "payments"} settled`}
            />
            <SummaryCard
              label="Awaiting payout"
              amount={summary.pending}
              currency={summary.currency}
              detail={
                summary.pending > 0
                  ? "Included in the next weekly run"
                  : "Nothing outstanding"
              }
            />
          </div>

          <div className="stack">
            <h2 className="headline-sm" id="payout-history-heading">
              Payout history
            </h2>
            {/* Below 600px app.css folds each row into a labelled panel, which
                is why every cell carries its column name — see the reflow rule
                there. Above it the table scrolls sideways, and a scrollable box
                whose contents hold no focus cannot be scrolled by keyboard at
                all — WCAG 2.1.1. tabIndex makes it reachable; the region role
                and its name stop it being an unlabelled stop on the way past. */}
            <div
              className="table-wrap"
              tabIndex={0}
              role="region"
              aria-labelledby="payout-history-heading"
            >
              {/* The roles are spelled out because the phone layout takes them
                  away: `display: block` on a <table> drops the implicit table,
                  row and cell roles in every browser, so the reflow would have
                  quietly turned a table into a pile of text for anyone using a
                  screen reader. Stated explicitly, they survive the layout —
                  and above 600px they say exactly what the tags already did. */}
              <table className="table" role="table">
                <caption className="sr-only">
                  Your payouts, newest first: date, job, status and amount.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Job</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="num">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {payouts.map((payout) => {
                    const status = parsePayoutStatus(payout.status);
                    return (
                      <tr key={payout.payoutId} role="row">
                        <td role="cell" data-label="Date">
                          {dateOnly(payout.completedAt ?? payout.createdAt)}
                        </td>
                        <td role="cell" data-label="Job">
                          {/* The web Payout DTO carries no jobTitle (the mobile
                              model invents one client-side), so the job id is
                              the only label available here — which is why it is
                              a link: eight hex characters identify nothing a
                              contractor has ever seen, and a finished job has
                              dropped off Active work, so this is the only route
                              back to what the money was for. contractor-api
                              still serves a completed job to its owner. */}
                          <Link className="link-inline truncate" to={`/jobs/${payout.jobId}`}>
                            Job #{payout.jobId.slice(0, 8)}
                          </Link>
                        </td>
                        <td role="cell" data-label="Status">
                          <StatusPill kind="payout" status={status} />
                        </td>
                        <td role="cell" className="num" data-label="Amount">
                          {money(payout.amount, payout.currency)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <Banner tone="info" title="How payouts work">
            Payouts are batched weekly and cover every job where the homeowner
            has confirmed completion. They land in the account under{" "}
            <Link className="link-inline" to="/profile">
              Payouts on your profile
            </Link>
            .
          </Banner>
        </div>
      ) : null}
    </>
  );
}
